

import { ProjectWithZones, ProjectStatus, buildingTypeMap, BuildingType, ProjectUpdate } from '@/features/projects/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Building, CalendarRange, FileText, Hash, Landmark, ListChecks, MapPin, Milestone, TrendingUp, UserCog, Edit, Save, X } from "lucide-react"; // アイコン追加
import React, { useState, useCallback } from 'react';
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { projectRepository } from '@/features/projects/repository';
import { validateProjectUpdate } from '@/shared/lib/validation/projectValidation';
import { errorHandler, ErrorLevel } from '@/shared/lib/error/errorHandler';
import { toast } from "sonner";
import { mutate } from 'swr';
import { ZoneListSection } from '@/features/projects/components/ZoneListSection';
import { ProjectZone } from '@/features/projects/types';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';

interface BasicInfoTabProps {
  projectData: ProjectWithZones;
  onProjectUpdated?: () => void; // 親コンポーネントへの更新通知
  updateProject?: (projectData: Partial<ProjectWithZones>) => Promise<boolean>; // プロジェクト更新関数
}

/**
 * 日付フォーマット
 * @param dateString 日付文字列
 * @returns 日付フォーマット
 */
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return <span className="text-muted-foreground">未設定</span>;
  try {
    return new Date(dateString).toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return <span className="text-destructive">日付エラー</span>;
  }
};

/**
 * ステータスバッジ
 * @param status ステータス
 * @returns ステータスバッジ
 */
const getStatusBadge = (status: string | null | undefined) => {
  switch (status) {
    case '進行中':
      return <Badge variant="default" className="bg-sky-500 hover:bg-sky-600 text-sky-50"><TrendingUp className="mr-1 h-3 w-3" />進行中</Badge>;
    case '完了':
      return <Badge variant="default" className="bg-green-500 hover:bg-green-600 text-green-50"><ListChecks className="mr-1 h-3 w-3" />完了</Badge>;
    case '保留':
      return <Badge variant="outline" className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"><Milestone className="mr-1 h-3 w-3" />保留</Badge>;
    case '中止':
      return <Badge variant="outline" className="border-slate-500 text-slate-600 hover:bg-slate-100"><UserCog className="mr-1 h-3 w-3" />中止</Badge>;
    default:
      return <Badge variant="outline">不明</Badge>;
  }
};

/**
 * 情報行
 * @param icon アイコン
 * @param label ラベル
 * @param value 値
 * @param fullWidth フル幅
 */
interface InfoRowProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}

/**
 * 情報行
 * @param icon アイコン
 * @param label ラベル
 * @param value 値
 * @param fullWidth フル幅
 */
const InfoRow: React.FC<InfoRowProps> = ({ icon: Icon, label, value, fullWidth = false }) => (
  <div className={`py-0.5 md:py-2 ${fullWidth ? 'col-span-1 md:col-span-2' : 'col-span-1'}`}>
    <dt className="mobile-xs md:text-sm font-medium text-muted-foreground flex items-center">
      <Icon className="mr-1 h-3 w-3 md:mr-2 md:h-4 md:w-4" />
      {label}
    </dt>
    <dd className="mt-0.5 md:mt-1 fluid-xs text-foreground ml-4 md:ml-6">{value !== null && value !== undefined && value !== '' ? value : <span className="text-muted-foreground">未設定</span>}</dd>
  </div>
);

export default function BasicInfoTab({ projectData, onProjectUpdated }: BasicInfoTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isZoneEditing, setIsZoneEditing] = useState(false);
  const [zones, setZones] = useState<ProjectZone[]>(projectData.project_zones || []);

  // 現在のユーザー情報を取得
  const { user: currentUser } = useCurrentUser();

  // projectDataが更新されたときにzonesを同期
  React.useEffect(() => {
    setZones(projectData.project_zones || []);
  }, [projectData.project_zones]);
  const [editedData, setEditedData] = useState({
    name: projectData.name || '',
    address: projectData.address || '',
    building_type: projectData.building_type || '住宅',
    start_date: projectData.start_date || '',
    end_date: projectData.end_date || '',
    notes: projectData.notes || '',
    status: projectData.status || '進行中'
  });

  // 編集モードの切り替え
  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      // 編集をキャンセル - 元のデータに戻す
      setEditedData({
        name: projectData.name || '',
        address: projectData.address || '',
        building_type: projectData.building_type || '住宅',
        start_date: projectData.start_date || '',
        end_date: projectData.end_date || '',
        notes: projectData.notes || '',
        status: projectData.status || '進行中'
      });
    }
    setIsEditing(!isEditing);
  }, [isEditing, projectData]);

  // 保存処理
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const updateData: ProjectUpdate = {
        name: editedData.name,
        address: editedData.address || null,
        building_type: editedData.building_type || null,
        start_date: editedData.start_date || null,
        end_date: editedData.end_date || null,
        notes: editedData.notes || null,
        status: editedData.status || null,
        updated_by: currentUser?.id || '00000000-0000-0000-0000-00000000a001'
      };

      // バリデーション実行
      const validation = validateProjectUpdate(updateData);
      if (!validation.success) {
        const errorMessage = validation.errors
          ? Object.values(validation.errors).flat().join(', ')
          : validation.error || 'バリデーションエラーが発生しました';
        throw new Error(errorMessage);
      }

      // 新しいプロジェクトリポジトリを使用
      await projectRepository.updateProject(projectData.id, updateData);

      // キャッシュを手動更新
      await Promise.all([
        // プロジェクト一覧のキャッシュを更新
        mutate(
          (key) => typeof key === 'string' && key.startsWith('/projects'),
          undefined,
          { revalidate: true }
        ),
        // プロジェクト詳細のキャッシュを更新
        mutate(
          (key) => typeof key === 'string' && key.includes(`/projects/${projectData.id}`),
          undefined,
          { revalidate: true }
        )
      ]);

      toast.success("プロジェクト情報を更新しました");
      setIsEditing(false);

      // 親コンポーネントに更新を通知
      if (onProjectUpdated) {
        onProjectUpdated();
      }
    } catch (error: unknown) {
      // エラーハンドリング
      const appError = errorHandler.handleError(
        error,
        {
          operation: 'updateProject',
          userId: currentUser?.id,
          tenantId: currentUser?.tenant_id,
          resourceId: projectData.id,
          additionalData: { editedData }
        },
        ErrorLevel.HIGH
      );

      console.error('Error updating project:', appError);
      toast.error(appError.message);
    } finally {
      setIsSaving(false);
    }
  }, [editedData, projectData.id, onProjectUpdated, currentUser?.id, currentUser?.tenant_id]);

  // 工区更新処理
  const handleZoneUpdate = useCallback(async (updatedZones: ProjectZone[]) => {
    try {
      // 既存の工区を更新
      for (const zone of updatedZones) {
        if (projectData.project_zones?.find(z => z.id === zone.id)) {
          await projectRepository.updateProjectZone(zone.id, {
            name: zone.zone_name || zone.name,
            area_sqm: zone.area ?? zone.area_sqm ?? null,
            notes: zone.notes ?? null,
          });
        } else if (zone.id.startsWith('zone-')) {
          // 新しい工区を作成
          await projectRepository.createProjectZone(projectData.id, {
            name: zone.zone_name || '新規工区',
            area_sqm: zone.area ?? null,
            notes: zone.notes ?? null,
            tenant_id: 'default', // tenant_idはバックエンドで設定される
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            id: '' as unknown as string, // 実際はAPI側で生成
            project_id: projectData.id,
          } as unknown as Omit<ProjectZone, 'id' | 'project_id' | 'created_at' | 'updated_at'>);
        }
      }

      // 削除された工区を処理
      const deletedZones = (projectData.project_zones || []).filter(
        oldZone => !updatedZones.find(newZone => newZone.id === oldZone.id)
      );

      for (const deletedZone of deletedZones) {
        await projectRepository.deleteProjectZone(deletedZone.id);
      }

      // キャッシュを更新
      await Promise.all([
        mutate(
          (key) => typeof key === 'string' && key.includes(`/projects/${projectData.id}`),
          undefined,
          { revalidate: true }
        ),
        mutate(
          (key) => typeof key === 'string' && key.startsWith('/projects'),
          undefined,
          { revalidate: true }
        )
      ]);

      toast.success("工区情報を更新しました");

      // 親コンポーネントに更新を通知
      if (onProjectUpdated) {
        onProjectUpdated();
      }
    } catch (error) {
      console.error('Error updating zones:', error);
      toast.error("工区情報の更新に失敗しました");
    }
  }, [projectData.id, projectData.project_zones, onProjectUpdated]);

  // 工区の変更を処理
  const handleZoneChange = useCallback((updatedZones: ProjectZone[]) => {
    setZones(updatedZones);
  }, []);

  // 工区編集の保存
  const handleZoneSave = useCallback(async () => {
    await handleZoneUpdate(zones);
    setIsZoneEditing(false);
  }, [zones, handleZoneUpdate]);

  // 工区編集のキャンセル
  const handleZoneCancel = useCallback(() => {
    setZones(projectData.project_zones || []);
    setIsZoneEditing(false);
  }, [projectData.project_zones]);

  return (
    <div className="space-y-3 md:space-y-6">
      <Card className="shadow-sm">
        <CardHeader className="p-3 md:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div className="flex-1">
              <CardTitle className="flex items-center fluid-sm md:text-lg">
                <FileText className="mr-2 h-4 w-4 md:h-5 md:w-5 text-primary" />
                プロジェクト概要
              </CardTitle>
              <CardDescription className="mobile-xs md:text-sm mt-1">プロジェクトの名称、ステータス、期間などの基本情報を確認できます。</CardDescription>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditToggle}
                    disabled={isSaving}
                    className="mobile-xs px-2 py-1"
                  >
                    <X className="h-3 w-3 sm:mr-1 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">キャンセル</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="mobile-xs px-2 py-1"
                  >
                    <Save className="h-3 w-3 sm:mr-1 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{isSaving ? '保存中...' : '保存'}</span>
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditToggle}
                  className="mobile-xs px-2 py-1"
                >
                  <Edit className="h-3 w-3 sm:mr-1 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">編集</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-6">
          {isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center mb-1">
                  <Hash className="mr-1 h-3 w-3 md:mr-2 md:h-4 md:w-4" />
                  物件名
                </label>
                <Input
                  value={editedData.name}
                  onChange={(e) => setEditedData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="プロジェクト名を入力"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center mb-2">
                  <ListChecks className="mr-2 h-4 w-4" />
                  ステータス
                </label>
                <Select
                  value={editedData.status}
                  onValueChange={(value) => setEditedData(prev => ({ ...prev, status: value as ProjectStatus }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="進行中">進行中</SelectItem>
                    <SelectItem value="完了">完了</SelectItem>
                    <SelectItem value="保留">保留</SelectItem>
                    <SelectItem value="中止">中止</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center mb-2">
                  <MapPin className="mr-2 h-4 w-4" />
                  住所
                </label>
                <Input
                  value={editedData.address}
                  onChange={(e) => setEditedData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="住所を入力"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center mb-2">
                  <Building className="mr-2 h-4 w-4" />
                  建築種別
                </label>
                <Select
                  value={editedData.building_type}
                  onValueChange={(value) => setEditedData(prev => ({ ...prev, building_type: value as BuildingType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="商業施設">商業施設</SelectItem>
                    <SelectItem value="住宅">住宅</SelectItem>
                    <SelectItem value="オフィス">オフィス</SelectItem>
                    <SelectItem value="分譲マンション">分譲マンション</SelectItem>
                    <SelectItem value="戸建住宅">戸建住宅</SelectItem>
                    <SelectItem value="工場・倉庫">工場・倉庫</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center mb-2">
                  <CalendarRange className="mr-2 h-4 w-4" />
                  開始日
                </label>
                <Input
                  type="date"
                  value={editedData.start_date}
                  onChange={(e) => setEditedData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center mb-2">
                  <CalendarRange className="mr-2 h-4 w-4" />
                  終了日
                </label>
                <Input
                  type="date"
                  value={editedData.end_date}
                  onChange={(e) => setEditedData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center mb-2">
                  <FileText className="mr-2 h-4 w-4" />
                  特記事項
                </label>
                <Textarea
                  value={editedData.notes}
                  onChange={(e) => setEditedData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="特記事項を入力"
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-3 md:gap-x-6 gap-y-1 md:gap-y-3">
              <InfoRow icon={Hash} label="物件名" value={projectData.name} />
              <InfoRow icon={ListChecks} label="ステータス" value={getStatusBadge(projectData.status || '進行中')} />
              <InfoRow icon={MapPin} label="住所" value={projectData.address} />
              <InfoRow icon={Building} label="建築種別" value={projectData.building_type ? (buildingTypeMap[projectData.building_type as keyof typeof buildingTypeMap] || projectData.building_type) : '住宅'} />
              <InfoRow icon={CalendarRange} label="開始日" value={formatDate(projectData.start_date)} />
              <InfoRow icon={CalendarRange} label="終了日" value={formatDate(projectData.end_date)} />
              <InfoRow icon={FileText} label="特記事項" value={projectData.notes} fullWidth />
            </dl>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="p-3 md:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div className="flex-1">
              <CardTitle className="flex items-center fluid-sm md:text-lg">
                <Landmark className="mr-2 h-4 w-4 md:h-5 md:w-5 text-primary" />
                工区情報
              </CardTitle>
              <CardDescription className="text-sm mt-1">
                このプロジェクトに登録されている工区の詳細と進捗を確認・編集できます。
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {isZoneEditing ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoneCancel}
                    className="mobile-xs px-2 py-1"
                  >
                    <X className="h-3 w-3 sm:mr-1 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">キャンセル</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleZoneSave}
                    className="mobile-xs px-2 py-1"
                  >
                    <Save className="h-3 w-3 sm:mr-1 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">保存</span>
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsZoneEditing(true)}
                  className="mobile-xs px-2 py-1"
                >
                  <Edit className="h-3 w-3 sm:mr-1 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">編集</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-6">
          <ZoneListSection
            zones={zones}
            onZoneChange={handleZoneChange}
            isEditMode={isZoneEditing}
          />
        </CardContent>
      </Card>
    </div>
  );
} 